import { formatRepoFiltersToApiFilters } from '@webapp/core/utils/repositories/formatRepoFiltersToApiFilters'
import { throwRepoErrorFromApiV3Error } from '@webapp/core/utils/repositories/throwRepoErrorFromApiV3Error'
import { type RepoFilterParams } from '@webapp/infrastructure/interfaces/repositories/RepoFilterParams'
import { type RepoPaginatedResponse } from '@webapp/infrastructure/interfaces/repositories/RepoPaginatedResponse'
import { type RepoStandardResponse } from '@webapp/infrastructure/interfaces/repositories/RepoStandardResponse'
import { type IHttpClient } from '@webapp/infrastructure/interfaces/services/IHttpClient'
import {
  type CreateVendorPaymentMethodInputData,
  type InviteVendorInputData,
  type IVendorRepository,
} from '@webapp/modules/vendor/business-logic/interfaces/IVendorRepository'
import { type VendorConstructor } from '@webapp/modules/vendor/entities/Vendor'
import { type VendorDocumentConstructor } from '@webapp/modules/vendor/entities/VendorDocument'
import { type VendorPaymentMethodConstructor } from '@webapp/modules/vendor/entities/VendorPaymentMethod'
import { type VendorRequestConstructor } from '@webapp/modules/vendor/entities/VendorRequest'
import { type VendorInviteApiModel } from '../../api-models/VendorInviteApiModel'
import { type VendorPaymentMethodApiModel } from '../../api-models/VendorPaymentMethodApiModel'
import { mapCreateVendorRequestResponseApiModel } from '../../mappers/mapCreateVendorRequestResponseApiModel'
import { mapVendorApiModel } from '../../mappers/mapVendorApiModel'
import { mapVendorDocumentApiModel } from '../../mappers/mapVendorDocumentApiModel'
import { mapVendorInviteApiModel } from '../../mappers/mapVendorInviteApiModel'
import { mapVendorPaymentMethodApiModel } from '../../mappers/mapVendorPaymentMethodApiModel'
import { mapVendorRequestApiModel } from '../../mappers/mapVendorRequestApiModel'
import { type CreateVendorInputData } from '../../types/CreateVendorInputData'
import { type CreateVendorPayload } from '../../types/CreateVendorPayload'
import { type CreateVendorRequestInputData } from '../../types/CreateVendorRequestInputData'
import { type CreateVendorRequestPayload } from '../../types/CreateVendorRequestPayload'
import { type CreateVendorRequestResponse } from '../../types/CreateVendorRequestResponse'
import { type ExportVendorsResponse } from '../../types/ExportVendorsResponse'
import { type GetVendorDocumentsResponse } from '../../types/GetVendorDocumentsResponse'
import { type GetVendorInvitesResponse } from '../../types/GetVendorInvitesResponse'
import { type GetVendorRequestResponse } from '../../types/GetVendorRequestResponse'
import { type GetVendorResponse } from '../../types/GetVendorResponse'
import { type GetVendorsResponse } from '../../types/GetVendorsResponse'
import { type UpdateVendorInputData } from '../../types/UpdateVendorInputData'
import { type UpdateVendorPayload } from '../../types/UpdateVendorPayload'
import { type VendorInvite } from '../../types/VendorInvite'

interface Dependency {
  httpClient: IHttpClient
}

export class VendorRepository implements IVendorRepository {
  constructor(private readonly deps: Dependency) {}

  async getVendors(
    params?: RepoFilterParams
  ): Promise<RepoPaginatedResponse<VendorConstructor>> {
    const fieldMapping = {
      type: 'type__in',
    } as const

    const formattedQueryParams: RepoFilterParams = params || {}

    if (params) {
      Object.entries(fieldMapping).forEach(([inputKey, outputKey]) => {
        if (params[inputKey] !== undefined) {
          formattedQueryParams[outputKey] = params[inputKey]
          formattedQueryParams[inputKey] = undefined
        }
      })
    }

    const res = await this.deps.httpClient.get<GetVendorsResponse>(
      '/api/v3/vendors/default/',
      {
        params: {
          ...formatRepoFiltersToApiFilters(formattedQueryParams),
          with_pending: true,
        },
        arrayParamFormat: 'none',
      }
    )

    const data = res.data.data
    const metadata = res.data.metadata

    return {
      data: data.map((d) => mapVendorApiModel(d)),
      pagination: {
        count: metadata?.pagination?.count || 0,
        page: metadata?.pagination?.current_page || 0,
        pageSize: metadata?.pagination?.page_size || 0,
        numPages: metadata?.pagination?.num_pages || 0,
      },
    }
  }

  async getVendor(
    id: number
  ): Promise<RepoStandardResponse<VendorConstructor>> {
    const res = await this.deps.httpClient.get<GetVendorResponse>(
      `/api/v3/vendors/${id}/`
    )

    const data = res.data.data

    return { data: mapVendorApiModel(data) }
  }

  async createVendor(
    data: CreateVendorInputData
  ): Promise<RepoStandardResponse<VendorConstructor>> {
    const formattedPayload: CreateVendorPayload = {
      name: data.name,
      email: data.email,
      external_id: data.externalId,
      contact: data.contact,
      comments: data.comments,
      addressLineOne: data.addressLineOne,
      addressLineTwo: data.addressLineTwo,
      city: data.city,
      country: data.country,
      payment_term_ref: data.paymentTermRef,
      phoneOne: data.phoneOne,
      phoneTwo: data.phoneTwo,
      fax: data.fax,
      postalCode: data.postalCode,
      preferred: data.preferred,
      shipping_method_ref: data.shippingMethodRef,
      shipping_term_ref: data.shippingTermRef,
      state_province: data.stateProvince,
      type: data.type,
      url: data.url,
      po_pdf_labels: data.poPdfLabels,
      default_payment_method: data.defaultPaymentMethod,
      tax: data.tax,
      is_auto_email_po_enabled: data.isAutoEmailPoEnabled,
    }

    try {
      const res = await this.deps.httpClient.post<
        GetVendorResponse,
        CreateVendorPayload
      >('/api/v3/vendors/', formattedPayload)

      const responseData = res.data.data

      return { data: mapVendorApiModel(responseData) }
    } catch (error) {
      throw throwRepoErrorFromApiV3Error(error)
    }
  }

  async updateVendor(id: number, data: UpdateVendorInputData): Promise<void> {
    // Mapping from camelCase input fields to snake_case API fields
    const fieldMapping: Record<
      keyof Omit<UpdateVendorInputData, 'emailConfigurations'>,
      keyof UpdateVendorPayload
    > = {
      name: 'name',
      email: 'email',
      externalId: 'external_id',
      contact: 'contact',
      comments: 'comments',
      addressLineOne: 'addressLineOne',
      addressLineTwo: 'addressLineTwo',
      city: 'city',
      country: 'country',
      paymentTermRef: 'payment_term_ref',
      phoneOne: 'phoneOne',
      phoneTwo: 'phoneTwo',
      fax: 'fax',
      postalCode: 'postalCode',
      preferred: 'preferred',
      shippingMethodRef: 'shipping_method_ref',
      shippingTermRef: 'shipping_term_ref',
      stateProvince: 'state_province',
      type: 'type',
      url: 'url',
      poPdfLabels: 'po_pdf_labels',
      defaultPaymentMethod: 'default_payment_method',
      tax: 'tax',
      isAutoEmailPoEnabled: 'is_auto_email_po_enabled',
    } as const

    const formattedPayload: Partial<
      Record<keyof UpdateVendorPayload, unknown>
    > = {}

    try {
      // Only include fields that are provided in the data
      Object.entries(fieldMapping).forEach(([inputKey, outputKey]) => {
        if (
          data.hasOwnProperty(inputKey) &&
          data[inputKey as keyof UpdateVendorInputData] !== undefined
        ) {
          formattedPayload[outputKey] =
            data[inputKey as keyof UpdateVendorInputData]
        }
      })

      // Handle emailConfigurations separately due to nested key transformation
      if (data.emailConfigurations !== undefined) {
        formattedPayload.email_configurations = Object.entries(
          data.emailConfigurations
        ).reduce(
          (acc, [email, prefs]) => ({
            ...acc,
            [email]: {
              auto_po: prefs.autoPo,
              payment_remittance: prefs.paymentRemittance,
            },
          }),
          {} as Record<
            string,
            { auto_po: boolean; payment_remittance: boolean }
          >
        )
      }

      await this.deps.httpClient.patch<Partial<UpdateVendorPayload>>(
        `/api/v3/vendors/${id}/`,
        formattedPayload
      )
    } catch (error) {
      throw throwRepoErrorFromApiV3Error(error)
    }
  }

  async exportVendors(shouldEmailExport: boolean = false): Promise<string> {
    try {
      const res = await this.deps.httpClient.post<ExportVendorsResponse>(
        '/api/v3/vendors/export/',
        { export_email: shouldEmailExport }
      )

      return res.data.data.user.email
    } catch (error) {
      throw throwRepoErrorFromApiV3Error(error)
    }
  }

  async deleteVendor(id: number): Promise<void> {
    try {
      await this.deps.httpClient.delete(`/api/v3/vendors/${id}/`)
    } catch (error) {
      throw throwRepoErrorFromApiV3Error(error)
    }
  }

  async disconnectVendor(vendorId: number): Promise<void> {
    try {
      // The server selects every connected invite of the vendor and
      // disconnects it. An empty `data` array means the vendor held no
      // connected invite, which is a successful no-op.
      await this.deps.httpClient.post<
        { data: VendorInviteApiModel[] },
        { vendor_id: number }
      >('/api/v3/vendor-invites/disconnect-vendor/', { vendor_id: vendorId })
    } catch (error) {
      throw throwRepoErrorFromApiV3Error(error)
    }
  }

  async createVendorRequest(
    data: CreateVendorRequestInputData
  ): Promise<RepoStandardResponse<VendorConstructor>> {
    const formattedPayload: CreateVendorRequestPayload = {
      name: data.name,
      external_id: data.externalId,
      type: data.type,
      reason: data.reason,
      contact: data.contact,
      url: data.url,
      phone_one: data.phoneOne,
      phone_two: data.phoneTwo,
      fax: data.fax,
      email: data.email,
      po_pdf_labels: data.poPdfLabels,
      is_auto_email_po_enabled: data.isAutoEmailPoEnabled,
      address_line_one: data.addressLineOne,
      address_line_two: data.addressLineTwo,
      postal_code: data.postalCode,
      state_province: data.stateProvince,
      shipping_method_ref: data.shippingMethodRef,
      shipping_term_ref: data.shippingTermRef,
      payment_term_ref: data.paymentTermRef,
      attachments: data.attachments,
      next_approver: data.nextApprover,
    }

    const res = await this.deps.httpClient.post<
      CreateVendorRequestResponse,
      CreateVendorRequestPayload
    >('/api/v3/vendors/request/', formattedPayload)

    const responseData = res.data.data

    return {
      data: mapCreateVendorRequestResponseApiModel(responseData),
    }
  }

  async getVendorRequest(
    id: number
  ): Promise<RepoStandardResponse<VendorRequestConstructor>> {
    const res = await this.deps.httpClient.get<GetVendorRequestResponse>(
      `/api/v3/vendors/request/${id}/`
    )

    const data = res.data.data

    return {
      data: mapVendorRequestApiModel(data),
    }
  }

  async updateVendorRequest(
    id: number,
    data: CreateVendorRequestInputData
  ): Promise<void> {
    const formattedPayload: CreateVendorRequestPayload = {
      name: data.name ?? '',
      external_id: data.externalId,
      type: data.type,
      reason: data.reason ?? '',
      contact: data.contact,
      url: data.url,
      phone_one: data.phoneOne,
      phone_two: data.phoneTwo,
      fax: data.fax,
      email: data.email ?? [],
      po_pdf_labels: data.poPdfLabels,
      is_auto_email_po_enabled: data.isAutoEmailPoEnabled,
      address_line_one: data.addressLineOne,
      address_line_two: data.addressLineTwo,
      postal_code: data.postalCode,
      state_province: data.stateProvince,
      shipping_method_ref: data.shippingMethodRef,
      shipping_term_ref: data.shippingTermRef,
      payment_term_ref: data.paymentTermRef,
      attachments: data.attachments,
      next_approver: data.nextApprover,
    }

    await this.deps.httpClient.put<CreateVendorRequestPayload>(
      `/api/v3/vendors/request/${id}/`,
      formattedPayload
    )
  }

  async deleteVendorRequest(id: number): Promise<void> {
    try {
      await this.deps.httpClient.delete(`/api/v3/vendors/request/${id}/`)
    } catch (error) {
      throw throwRepoErrorFromApiV3Error(error)
    }
  }

  async getVendorDocuments(
    vendorId: number,
    params?: RepoFilterParams
  ): Promise<RepoPaginatedResponse<VendorDocumentConstructor>> {
    const res = await this.deps.httpClient.get<GetVendorDocumentsResponse>(
      `/api/v3/vendors/${vendorId}/attachments/`,
      { params: formatRepoFiltersToApiFilters(params) }
    )

    const data = res.data.data
    const metadata = res.data.metadata

    return {
      data: data.map((d) => mapVendorDocumentApiModel(d)),
      pagination: {
        count: metadata?.pagination?.count || 0,
        page: metadata?.pagination?.current_page || 0,
        pageSize: metadata?.pagination?.page_size || 0,
        numPages: metadata?.pagination?.num_pages || 0,
      },
    }
  }

  async createVendorDocuments(
    vendorId: number,
    attachmentUuids: uuid[]
  ): Promise<void> {
    await this.deps.httpClient.post<void, { attachments: uuid[] }>(
      `/api/v3/vendors/${vendorId}/attachments/`,
      { attachments: attachmentUuids }
    )
  }

  async inviteVendor(
    vendorId: number,
    data: InviteVendorInputData
  ): Promise<
    RepoStandardResponse<{
      id: string
      vendorId: number
      contactEmail: string
      status: string
    }>
  > {
    try {
      const res = await this.deps.httpClient.post<
        {
          data: {
            id: string
            vendor_id: number
            contact_email: string
            status: string
          }
        },
        {
          vendor_id: number
          contact_email: string
          collect_tax_documents: boolean
          email_message: string
        }
      >('/api/v3/vendor-invites/', {
        vendor_id: vendorId,
        contact_email: data.email,
        collect_tax_documents: data.collectTaxDocuments,
        email_message: data.emailMessage,
      })

      const responseData = res.data.data

      return {
        data: {
          id: responseData.id,
          vendorId: responseData.vendor_id,
          contactEmail: responseData.contact_email,
          status: responseData.status,
        },
      }
    } catch (error) {
      throw throwRepoErrorFromApiV3Error(error)
    }
  }

  async getHeadquartersLocationName(): Promise<string | null> {
    // The runtime /api/v3/global/ response omits `company` (the
    // GlobalAPIResponse interface still declares it, but the field is stale);
    // the org's display name lives on the headquarter location, the same
    // source the invite email subject uses server-side.
    const res = await this.deps.httpClient.get<{
      data: { id: number; name: string }[]
    }>('/api/v2/locations/', { params: { headquarter: true } })

    return res.data.data[0]?.name ?? null
  }

  async getVendorInvites(
    vendorId: number
  ): Promise<RepoStandardResponse<VendorInvite[]>> {
    // page_size=all: the endpoint paginates (default 10), so fetch every
    // invite — a `connected` one past the first page must not be missed.
    const res = await this.deps.httpClient.get<GetVendorInvitesResponse>(
      '/api/v3/vendor-invites/',
      { params: { vendor_id: vendorId, page_size: 'all' } }
    )

    return { data: res.data.data.map((d) => mapVendorInviteApiModel(d)) }
  }

  async cancelVendorInvite(inviteId: string): Promise<void> {
    try {
      await this.deps.httpClient.post(
        `/api/v3/vendor-invites/${inviteId}/cancel/`
      )
    } catch (error) {
      throw throwRepoErrorFromApiV3Error(error)
    }
  }

  async getVendorPaymentMethods(
    vendorId: number
  ): Promise<VendorPaymentMethodConstructor[]> {
    try {
      const res = await this.deps.httpClient.get<{
        data: { payment_methods: VendorPaymentMethodApiModel[] }
      }>(`/api/v3/vendors/${vendorId}/`)

      return (res.data.data.payment_methods ?? []).map(
        mapVendorPaymentMethodApiModel
      )
    } catch (error) {
      throw throwRepoErrorFromApiV3Error(error)
    }
  }

  // Provider options for CreditCardProviderSelect (ADR-3 select; EADR-4
  // colocated value object) — the client reads only id/name from the full
  // type-6 vendor rows.

  async createVendorPaymentMethod(
    vendorId: number,
    data: CreateVendorPaymentMethodInputData
  ): Promise<VendorPaymentMethodConstructor> {
    try {
      const res = await this.deps.httpClient.post<{
        data: VendorPaymentMethodApiModel
      }>('/api/v2/ap/vendor-payment-methods/', {
        vendor: vendorId,
        name: data.name,
        type: data.type,
        currency: data.currencyId,
        data: {},
      })

      return mapVendorPaymentMethodApiModel(res.data.data)
    } catch (error) {
      throw throwRepoErrorFromApiV3Error(error)
    }
  }
}
